<template>
  <div id="distributionBoard">
    <div v-if="totalSupply" class="distribution">
      <div class="title">
        {{ $t('EvCharging.Distributions.DeviceLoad') }}
      </div>
      <div class="distribution-color-info">
        <!-- 顏色提示圖例 -->
        <div class="legend">
          <div class="legend-item">
            <span class="legend-box normal"></span>  {{ $t('EvCharging.Distributions.Normal') }}
          </div>
          <div class="legend-item">
            <span class="legend-box warning"></span> {{ $t('EvCharging.Distributions.Warning') }}
          </div>
          <div class="legend-item">
            <span class="legend-box overload"></span> {{ $t('EvCharging.Distributions.Abnormal') }}
          </div>
        </div>
      </div>
    </div>
    <!-- 總供電資訊 -->
    <div v-if="totalSupply" class="total-supply">
      <h3>{{ $t('EvCharging.Distributions.TotalPower') }}</h3>
      <div>
        <p>{{ totalSupply.TotalCapacity }}kW</p>
        <p
          :class="isOverload ? 'overload' : 'normal'"
        >
          {{ totalSupply.TotalConsumption }}/{{ totalSupply.TotalCapacity }} ({{ totalSupply.UsagePercentage.toFixed(1) }}%)
        </p>
      </div>
    </div>
    <!-- 連接線 -->
    <div v-if="floors.length>0" class="line"></div>
    <!-- 樓層與分電盤 -->
    <div v-if="floors.length>0" class="distribution-boards">
      <div>
        <div
          v-for="floor in floors"
          :key="floor.FloorName"
        >
          <h3>{{ floor.FloorName }}</h3>
          <div class="floor-section">
            <div
              v-for="board in floor.DistributionBoards"
              :key="board.DistributionId"
              class="board"
              :class="getBoardClass(board.Status.StatusCode)"
              @click="openBoardDetail(board)"
            >
              <h4>{{ board.Name }}</h4>
              <p>{{ board.CurrentPower }}/{{ board.MaxPower }} kW ({{ ((board.CurrentPower / board.MaxPower) * 100).toFixed(1) }}%)</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 分電盤詳細資料彈窗 -->
    <el-dialog
      :title="selectedBoard ? `${selectedBoard.Name} ${$t('EvCharging.Distributions.Details')}` : $t('EvCharging.Distributions.Details') "
      :visible.sync="dialogVisible"
      width="40%"
      @closed="handleDialogClose"
    >
      <table v-if="selectedBoard" class="details-table">
        <tbody>
          <tr v-for="([key, value], index) in filteredStatus" :key="index">
            <td class="details-table__cell">
              {{ translateKey(key) }}
            </td>
            <td
              class="details-table__cell"
              :class="key === 'StatusDescription' ? getBoardClass(selectedBoard.Status.StatusCode) : ''"
            >
              {{ formatValue(key, value) }}
            </td>
          </tr>
        </tbody>
      </table>
      <el-table v-if="expandedChargeList.length" class="connector-table" :data="expandedChargeList" style="margin-top: 20px;">
        <el-table-column prop="connectorSn" :label="$t('EvCharging.Connector.ConnectorSn')" width="160" />
        <el-table-column :label="$t('EvCharging.Connector.ChargeName')">
          <template #default="{ row }">
            <span>{{ row.chargeName }}</span>
          </template>
        </el-table-column>

        <el-table-column :label="$t('EvCharging.Connector.Status')">
          <template #default="{ row }">
            <div
              :class="{
                'connectorStatus-error': [5, 6, 7, 8].includes(row.connectorStatus),
                'connectorStatus-charging': [3].includes(row.connectorStatus)
              }"
            >
              {{ connectorStatusEnum[row.connectorStatus] }}
            </div>
          </template>
        </el-table-column>
        <el-table-column :label="$t('EvCharging.Distributions.AbnormalContent')">
          <template #default="{ row }">
            <span
              :class="getBoardClass(row.statusCode)"
            > {{ row.chargeMes }} </span>
          </template>
        </el-table-column>
      </el-table>
    </el-dialog>
  </div>
</template>

<script>
import { apiEvCharging } from '@/api/EvCharging'

export default {
  props: {
    chargingFeature: {
      type: Object,
      required: false
    }
  },
  data () {
    return {
      connectorStatusEnum: {
        1: this.$t('EvCharging.Connector.Idle'),
        2: this.$t('EvCharging.Connector.Preparing'),
        3: this.$t('EvCharging.Connector.Charging'),
        4: this.$t('EvCharging.Connector.Stopped'),
        5: this.$t('EvCharging.Connector.Unavailable'),
        6: this.$t('EvCharging.Connector.Error'),
        7: this.$t('EvCharging.Connector.Offline'),
        8: this.$t('EvCharging.Connector.EmergencyStop')
      },
      totalSupply: null,
      floors: [],
      dialogVisible: false,
      selectedBoard: null,
      intervalId: null // 用於定時器
    }
  },
  computed: {
    isOverload () {
      return this.totalSupply?.UsagePercentage >= 100
    },
    boardsWithUsagePercentage () {
      return this.distributionBoards.map(board => ({
        ...board,
        powerUsagePercentage: ((board.currentPower / board.maxPower) * 100).toFixed(1)
      }))
    },
    filteredStatus () {
      const excludedKeys = ['StatusCode', 'Temperature'] // 排除狀態碼
      if (!this.selectedBoard || !this.selectedBoard.Status) return []
      return Object.entries(this.selectedBoard.Status).filter(([key]) => !excludedKeys.includes(key))
    },
    // 展開充電盤內部的充電樁列表
    expandedChargeList () {
      return this.selectedBoard && this.selectedBoard.ChargeList.length > 0
        ? this.selectedBoard.ChargeList.flatMap(item =>
          (item.ConnectorList || []).map(connector => ({
            // StatusAlertType => 是樁體的錯誤顯示 1正常 2 警告 3 異常
            statusCode: item.StatusAlertType,
            chargeName: item.ChargeName,
            chargeMes: item.ChargeMes,
            connectorSn: connector.ConnectorSn,
            connectorStatus: connector.ConnectorStatus
          }))
        )
        : []
    }
  },
  watch: {
    chargingFeature: {
      immediate: true,
      handler (val) {
        if (val?.HasDistributor) {
          this.fetchDashboardData()
          this.startAutoUpdate()
        } else {
          this.totalSupply = null
          this.floors = []
        }
      }
    }
  },
  destroyed () {
    this.stopAutoUpdate()
  },
  methods: {
    // 獲取儀表板數據
    fetchDashboardData () {
      apiEvCharging.PowerDistribution.getDistributorsData().then(res => {
        if (res?.data.Data) {
          this.totalSupply = res.data.Data.TotalSupply
          this.floors = res.data.Data.Floors
        }
      }).catch(err => {
        console.error('Failed to fetch dashboard data:', err)
      })
    },
    // 打開分電盤詳細資料彈窗
    openBoardDetail (board) {
      this.selectedBoard = board
      this.dialogVisible = true
    },
    // 關閉詳細資料彈窗
    handleDialogClose () {
      this.selectedBoard = null
    },
    // 啟動自動更新
    startAutoUpdate () {
      // 若已有計時器，先清除它
      if (this.intervalId) {
        clearInterval(this.intervalId)
      }
      this.intervalId = setInterval(() => {
        this.fetchDashboardData()
      }, 60 * 1000) // 每分鐘更新一次
    },
    // 停止自動更新
    stopAutoUpdate () {
      if (this.intervalId) {
        clearInterval(this.intervalId)
      }
    },
    // 根據狀態代碼返回對應的類名
    getBoardClass (statusCode) {
      if (statusCode === 1) return 'normal'
      if (statusCode === 2) return 'warning'
      if (statusCode === 3) return 'overload'
      return '' // 默認為無狀態
    },
    translateKey (key) {
      const translations = {
        Voltage: this.$t('EvCharging.Distributions.Voltage'),
        Current: this.$t('EvCharging.Distributions.Current'),
        Power: this.$t('EvCharging.Distributions.Power'),
        StatusDescription: this.$t('EvCharging.Distributions.StatusDescription')
      }
      return translations[key] || key
    },
    formatValue (key, value) {
      if (key === 'Voltage') return `${value} V`
      if (key === 'Current') return `${value} A`
      if (key === 'Power') return `${value} kW`
      if (key === 'StatusDescription') return value || this.$t('EvCharging.Distributions.Normal')
      return value
    }
  }
}
</script>

<style lang="scss" scoped>
#distributionBoard {
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  .distribution{
    width: 100%;
    display: flex;
    justify-content: space-between;
    .title{
      font-size: map-get($fontSize, large);
      font-weight: 600;
      color: $mainFontColor;
      padding: 0 0 16px;
      text-align: start;
    }
    &-color-info {
      width: fit-content;
      display: flex;
      justify-content: flex-end;
      .legend{
        display: flex;
        gap: 20px;
        .legend-item{
          display: flex;
          align-items: center;
          font-size: 14px;
        }
        .legend-box{
          display: inline-block;
          width: 20px;
          height: 20px;
          margin-right: 8px;
          border-radius: 4px;

          &.overload {
            border-color: $dangerColor;
            background-color: $evChargingOverload;
          }
          &.normal {
            background-color: $evChargingNormal;
            border-color: $evChargingNormalBorder;
          }

          &.warning {
            background-color: $evChargingWarningBackground;
            border-color: $evChargingWarningBackground;
          }
        }
      }
    }
  }
  .total-supply {
    text-align: center;
    line-height: 24px;
    border: 1px solid $borderDark1;
    border-radius: 10px;
    width: fit-content;
    padding: 16px 60px;
    align-items: center;
    .overload {
      color: $dangerColor;
    }
    .normal {
      color: $mainColor;
    }
    .warning {
      color: $evChargingWarning;
    }
  }
  .line {
    width: 1px;
    height: 20px;
    background: $borderDark1;
  }
  .distribution-boards {
    border: 1px solid $borderDark1;
    width: -webkit-fill-available;
    border-radius: 10px;
    display: flex;
    gap: 20px;
    padding: 20px;
    flex-wrap: wrap;
    & > div {
      display: flex;
      gap: 20px;
      padding: 20px 40px;
      flex-wrap: wrap;
      & > div > h3{
      margin-bottom: 10px;
     }
    }
  }
  .floor-section  {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    padding: 20px;
    border: 1px solid $borderDark1;
    border-radius: 10px;
    max-width: 200px;
    gap:20px;
    .board {
      width: 200px;
      padding: 15px;
      border: 1px solid #ccc;
      border-radius: 10px;
      text-align: center;
      cursor: pointer;
      background-color: $bgDarkBg;
      gap:20px;

      &.overload {
        border-color: $dangerColor;
        background-color: $evChargingOverload;
      }
      &.normal {
        background-color: $evChargingNormal;
        border-color: $evChargingNormalBorder;
      }

      &.warning {
        background-color: $evChargingWarningBackground;
        border-color: $evChargingWarningBackground;
      }

      h4 {
        margin-bottom: 10px;
      }
    }

  }
  .details-table {
    width: 100%;
    border-collapse: collapse;

    &__header {
      text-align: left;
      padding: 8px;
      border-bottom: 1px solid #ccc;
      font-weight: bold;
      font-size: 20px;
    }

    &__cell {
      text-align: left;
      padding: 8px;
      border-bottom: 1px solid #eee;
      font-size: 16px;
      min-width: 150px;
    }
    .connectorStatus {
      &-charging {
        color: $mainColor;
      }
      &-error {
        color: $dangerColor;
      }
    }
    .overload {
      color: $dangerColor;
    }
    .normal {
      color: $mainColor;
    }
    .warning {
      color: $evChargingWarning;
    }
  }
  .connector-table {
    .connectorStatus {
      &-charging {
        color: $mainColor;
      }
      &-error {
        color: $dangerColor;
      }
    }
    .overload {
      color: $dangerColor;
    }
    .normal {
      color: $mainColor;
    }

    .warning {
      color: $evChargingWarning;
    }
  }
  ::v-deep .el-table {
    @include basicTable(false);
  }
  ::v-deep .el-dialog{
    @include basicDialog();
  }
}
</style>
